/**
 * Lightweight schema / validation helpers for RefundRocket.
 *
 * We don't pull in zod for this single tool — the surface is small and
 * a hand-rolled validator keeps the bundle lean. The interface mirrors
 * a typical zod-style usage (`safeParse`, `parse`, issue array) so we
 * can swap in zod later without touching callers.
 */

import type { FilingStatus } from '../data/federal-2026';

export const FILING_STATUSES: readonly FilingStatus[] = [
  'single',
  'mfj',
  'mfs',
  'hoh',
] as const;

export interface CalcInput {
  filingStatus: FilingStatus;
  dependents: number;
  wages: number;
  federalWithholding: number;
  retirement401k: number;
  hsaContribution: number;
  studentLoanInterest: number;
}

export interface ValidationIssue {
  path: (keyof CalcInput)[];
  message: string;
}

export interface ParseResult<T> {
  success: boolean;
  data?: T;
  issues: ValidationIssue[];
}

const isFilingStatus = (v: unknown): v is FilingStatus =>
  typeof v === 'string' && (FILING_STATUSES as readonly string[]).includes(v);

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const isNonNegativeInt = (v: unknown): v is number =>
  isFiniteNumber(v) && Number.isInteger(v) && v >= 0;

/** Coerce unknown input into a CalcInput, collecting issues. */
export function parseCalcInput(raw: unknown): ParseResult<CalcInput> {
  const issues: ValidationIssue[] = [];
  if (typeof raw !== 'object' || raw === null) {
    return {
      success: false,
      issues: [{ path: [], message: 'Input must be an object.' }],
    };
  }
  const r = raw as Record<string, unknown>;

  const filingStatus: FilingStatus = isFilingStatus(r.filingStatus)
    ? r.filingStatus
    : ((): FilingStatus => {
        issues.push({
          path: ['filingStatus'],
          message: `filingStatus must be one of: ${FILING_STATUSES.join(', ')}.`,
        });
        return 'single';
      })();

  const dependents = isNonNegativeInt(r.dependents) && r.dependents <= 20
    ? r.dependents
    : (() => {
        issues.push({
          path: ['dependents'],
          message: 'dependents must be an integer between 0 and 20.',
        });
        return 0;
      })();

  const moneyField = (
    key: keyof CalcInput,
    rawValue: unknown,
    max: number,
  ): number => {
    if (!isFiniteNumber(rawValue)) {
      issues.push({
        path: [key],
        message: `${key} must be a finite number.`,
      });
      return 0;
    }
    if (rawValue < 0) {
      issues.push({
        path: [key],
        message: `${key} cannot be negative.`,
      });
      return 0;
    }
    if (rawValue > max) {
      issues.push({
        path: [key],
        message: `${key} exceeds the reasonable maximum of ${max}.`,
      });
      return max;
    }
    return rawValue;
  };

  const wages = moneyField('wages', r.wages, 10_000_000);
  const federalWithholding = moneyField('federalWithholding', r.federalWithholding, 10_000_000);
  const retirement401k = moneyField('retirement401k', r.retirement401k, 100_000);
  const hsaContribution = moneyField('hsaContribution', r.hsaContribution, 100_000);
  const studentLoanInterest = moneyField(
    'studentLoanInterest',
    r.studentLoanInterest,
    10_000,
  );

  if (issues.length > 0) {
    return { success: false, issues };
  }
  return {
    success: true,
    data: {
      filingStatus,
      dependents,
      wages,
      federalWithholding,
      retirement401k,
      hsaContribution,
      studentLoanInterest,
    },
    issues: [],
  };
}

/** Build a CalcInput from raw form strings (e.g. from <input> values). */
export function fromFormValues(form: {
  filingStatus: string;
  dependents: string;
  wages: string;
  federalWithholding: string;
  retirement401k: string;
  hsaContribution: string;
  studentLoanInterest: string;
}): CalcInput {
  return {
    filingStatus: isFilingStatus(form.filingStatus) ? form.filingStatus : 'single',
    dependents: Math.max(0, Math.min(20, Math.floor(Number(form.dependents) || 0))),
    wages: Math.max(0, Number(form.wages) || 0),
    federalWithholding: Math.max(0, Number(form.federalWithholding) || 0),
    retirement401k: Math.max(0, Number(form.retirement401k) || 0),
    hsaContribution: Math.max(0, Number(form.hsaContribution) || 0),
    studentLoanInterest: Math.max(0, Number(form.studentLoanInterest) || 0),
  };
}